import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Clock, RefreshCw, Send, Star, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { API } from "@/lib/api";

const STATUS_STYLE = {
  APPROVED: "bg-emerald-50 text-emerald-700",
  PENDING: "bg-amber-50 text-amber-700",
  REJECTED: "bg-red-50 text-red-700",
};

const VAR_HINT = "name, program, batch, url, testurl, website, date, constant1, constant2, constant3, mobile, email, place, date, zoom_link, zoom_meeting_id, zoom_passcode, batch_no, batch_dates, batch_time — or any fixed text";
const inputCls = "bg-white/80 border border-border rounded-full px-4 py-1.5 text-sm outline-none focus:border-accent";

const StatusIcon = ({ status }) => {
  if (status === "APPROVED") return <CheckCircle2 size={14} className="text-emerald-600" />;
  if (status === "REJECTED") return <XCircle size={14} className="text-red-600" />;
  return <Clock size={14} className="text-amber-600" />;
};

const TemplateSelect = ({ testId, value, templates, onChange }) => (
  <select
    data-testid={testId}
    value={templates.some((t) => t.name === value) ? value : ""}
    onChange={(e) => onChange(e.target.value)}
    className="mt-1 w-full bg-white border border-border rounded-lg px-2 py-1.5 text-sm outline-none focus:border-accent"
  >
    <option value="">{templates.length ? "— choose a template —" : "No templates found in your WhatsApp Business Account"}</option>
    {templates.map((t) => (
      <option key={`${t.name}-${t.language}`} value={t.name}>{t.name} · {t.language} · {t.status}</option>
    ))}
  </select>
);

const TemplateBlock = ({ kind, title, heading, hint, content, setC, templates, testMobile, adminKey, enabled, onContentSaved }) => {
  const [testing, setTesting] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  const nameKey = `wa_${kind}_template_name`;
  const langKey = `wa_${kind}_template_lang`;
  const varsKey = `wa_${kind}_template_vars`;
  const name = content[nameKey] || "";
  const varsText = typeof content[varsKey] === "string" ? content[varsKey] : "";
  const mappedCount = varsText.split(",").filter((x) => x.trim()).length;
  const tpl = templates.find((t) => t.name === name);

  const pick = (v) => {
    setC(nameKey, v);
    const t = templates.find((x) => x.name === v);
    if (t?.language) setC(langKey, t.language);
  };

  const sendTest = async () => {
    if (!testMobile.trim()) { toast.error("Enter a WhatsApp number for the test"); return; }
    setTesting(true);
    try {
      const res = await fetch(`${API}/admin/whatsapp-api/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-PL-Client": "web", "X-Admin-Key": adminKey },
        body: JSON.stringify({ mobile: testMobile.trim(), name: "Test Participant", kind }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed");
      if (data.receipt_status === "failed") toast.error(`Meta accepted the test but could not deliver it — ${data.receipt_error || "no reason given"}`, { duration: 12000 });
      else if (["delivered", "read"].includes(data.receipt_status)) toast.success(`Test ${title.toLowerCase()} delivered on WhatsApp`);
      else toast.success(`Test ${title.toLowerCase()} accepted by WhatsApp — delivery receipt not in yet; check the Delivery receipts card`);
    } catch (err) {
      toast.error(err.message || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const setDefault = async () => {
    setSettingDefault(true);
    try {
      const res = await fetch(`${API}/admin/whatsapp-api/default-template`, {
        method: "PUT", headers: { "Content-Type": "application/json", "X-PL-Client": "web", "X-Admin-Key": adminKey },
        body: JSON.stringify({ template_name: name, language: content[langKey] || "en", variables: varsText }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed");
      onContentSaved?.(data);
      toast.success(`"${name}" is now the default welcome template`);
    } catch (err) {
      toast.error(err.message || "Could not set default");
    } finally {
      setSettingDefault(false);
    }
  };

  return (
    <div className="rounded-lg bg-white/70 border border-border px-3 py-3 space-y-2" data-testid={`wa-api-${kind}-block`}>
      <div className="flex items-center justify-between gap-2">
        {heading
          ? <p data-testid={`wa-api-${kind}-heading`} className="inline-flex items-center rounded-md bg-[#1A2E26] text-white px-3 py-1.5 text-sm font-bold tracking-wide shadow-sm border-l-4 border-[#C86A51]">{heading}</p>
          : <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{title}</p>}
        <span data-testid={`wa-api-${kind}-status`} className={`inline-flex items-center gap-1 text-[10px] rounded-full px-1.5 py-0.5 ${STATUS_STYLE[tpl?.status] || "bg-muted text-muted-foreground"}`}>
          <StatusIcon status={tpl?.status} /> {tpl?.status || "not found"}
        </span>
      </div>
      <TemplateSelect testId={`wa-api-${kind}-select`} value={name} templates={templates} onChange={pick} />
      {tpl?.body && <pre data-testid={`wa-api-${kind}-body`} className="whitespace-pre-wrap font-sans text-[11px] text-muted-foreground bg-muted/40 rounded-lg px-2 py-1.5 max-h-28 overflow-auto">{tpl.body}</pre>}
      {tpl && tpl.var_count !== undefined && tpl.var_count !== mappedCount && (
        <p className="text-[11px] text-red-700" data-testid={`wa-api-${kind}-var-mismatch`}>This template has {tpl.var_count} variable(s) but {mappedCount} are mapped below — fix the mapping so Meta accepts the message.</p>
      )}
      {tpl?.category === "MARKETING" && (
        <p className="text-[11px] text-red-700" data-testid={`wa-api-${kind}-marketing-warning`}>⚠ This is a <strong>MARKETING</strong> template: Meta does not deliver marketing templates to US (+1) numbers and filters them more aggressively — Meta still reports "sent". Use a <strong>UTILITY</strong> template{kind === "welcome" ? " (e.g. positivelife_welcome)" : ""} for reliable delivery.</p>
      )}
      {tpl?.status === "PENDING" && <p className="text-[11px] text-amber-800" data-testid={`wa-api-${kind}-pending`}>Meta is still reviewing this template — sends work as soon as it is APPROVED.</p>}
      {tpl?.rejected_reason && tpl.rejected_reason !== "NONE" && <p className="text-[11px] text-red-700">Rejected: {tpl.rejected_reason}</p>}
      <div className="flex flex-wrap gap-2 items-end">
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Language</span>
          <input data-testid={`content-${langKey}`} value={content[langKey] || ""} onChange={(e) => setC(langKey, e.target.value)} placeholder="en" className={`${inputCls} mt-1 w-24 block`} />
        </label>
        <label className="block flex-1 min-w-[220px]">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Body variables {"{{1}}, {{2}}…"} in order</span>
          <input data-testid={`content-${varsKey}`} value={varsText} onChange={(e) => setC(varsKey, e.target.value)} placeholder="leave empty if the template has no variables" className={`${inputCls} mt-1 w-full block`} />
        </label>
        <button type="button" data-testid={`wa-api-${kind}-test-btn`} onClick={sendTest} disabled={testing || !enabled} className="inline-flex items-center gap-1.5 rounded-full bg-[#128C7E] text-white px-4 py-1.5 text-xs font-medium hover:bg-[#0f7568] transition-colors disabled:opacity-50">
          <Send size={12} /> {testing ? "Sending…" : "Send test"}
        </button>
        {kind === "welcome" && (
          <button type="button" data-testid="wa-api-set-default-btn" onClick={setDefault} disabled={settingDefault || !name} title="Save this template, language and variables as the default welcome message right now" className="inline-flex items-center gap-1.5 rounded-full border border-[#1A2E26] text-[#1A2E26] px-4 py-1.5 text-xs font-medium hover:bg-[#1A2E26] hover:text-white transition-colors disabled:opacity-50">
            <Star size={12} /> {settingDefault ? "Saving…" : "Set as default"}
          </button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );
};

const WebhookCard = ({ wh, adminKey, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const url = wh.callback_here || `${window.location.origin}${wh.path}`;
  const here = !!wh.pointed_here;
  const state = here ? "connected to this site" : wh.subscribed ? "pointing to another site" : "not connected";
  const connect = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/admin/whatsapp-api/connect-receipts`, { method: "POST", headers: { "X-PL-Client": "web", "X-Admin-Key": adminKey } });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.detail || `HTTP ${res.status}`);
      toast.success("Delivery receipts now come to this site — new sends will show delivered / failed");
      onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className={`mt-3 rounded-lg border px-3 py-3 text-sm ${here ? "bg-white/70 border-border" : "bg-amber-50 border-amber-300"}`} data-testid="wa-api-webhook">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium flex items-center gap-2">Delivery receipts (Meta webhook)
          <span data-testid="wa-api-webhook-state" className={`text-[10px] rounded-full px-1.5 py-0.5 ${here ? "bg-emerald-50 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{state}</span>
        </p>
        <div className="flex items-center gap-2">
          {wh.last_event_at && <span className="text-[11px] text-muted-foreground">Last receipt: {new Date(wh.last_event_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} ({wh.last_status}) · {wh.events_7d} in 7 days / {wh.failed_7d} failed</span>}
          {!here && <button type="button" data-testid="wa-api-connect-receipts" onClick={connect} disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-[#128C7E] text-white px-3 py-1 text-xs font-semibold hover:opacity-90 disabled:opacity-50">{busy ? "Connecting…" : "Connect receipts to this site"}</button>}
        </div>
      </div>
      {!here && <p className="text-[11px] text-amber-900 mt-1" data-testid="wa-api-webhook-warning">Without receipts, a chip only says <strong>sent · awaiting delivery</strong> — Meta accepted the message but we never learn whether it reached the phone or why it failed. {wh.preview ? "This is the preview site; connect from the live website so receipts go there." : wh.has_app_secret ? "Click the button once — it tells Meta to send receipts here." : "Click the button; if Meta refuses, do the one-time manual setup below (or add the Meta App Secret on the server and the button will do it for you)."}{wh.callback && !here ? <> Currently pointing to <code className="break-all">{wh.callback}</code>.</> : null}</p>}
      {wh.last_failure && <div className="mt-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-900" data-testid="wa-api-last-failure">
        <p className="font-semibold">WhatsApp is accepting messages but NOT delivering them</p>
        <p className="mt-0.5 break-words">{wh.last_failure.error.split(/(https?:\/\/\S+)/).map((part, i) => /^https?:\/\//.test(part) ? <a key={i} href={part} target="_blank" rel="noreferrer" className="underline font-medium">Open Meta billing / fix it here</a> : part)}</p>
        <p className="mt-0.5 text-[11px] text-red-800/80">Last failed {new Date(wh.last_failure.received_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} · {wh.failed_7d} failed in 7 days. Fix the issue at Meta, then use "Resend welcome to all failed" in Registrations.</p>
      </div>}
      {here && <p className="text-[11px] text-muted-foreground mt-1">Each registrant's chip now shows <strong>delivered</strong> / <strong>read</strong> / <strong>failed</strong> (with Meta's reason) once WhatsApp reports back — usually within a minute.</p>}
      {wh.receipts_error && <p className="text-[11px] text-red-700 mt-1">{wh.receipts_error}</p>}
      <details className="mt-2 text-[11px] text-muted-foreground"><summary className="cursor-pointer">Manual setup (if the button fails)</summary>
        <div className="mt-2 grid sm:grid-cols-2 gap-2">
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Callback URL</span>
            <div className="flex gap-1 mt-1"><code data-testid="wa-api-webhook-url" className="flex-1 text-xs bg-muted/60 rounded-lg px-2 py-1.5 break-all">{url}</code><button type="button" onClick={() => { navigator.clipboard?.writeText(url); toast.success("Copied"); }} className="rounded-lg border border-border bg-white px-2 text-xs hover:bg-muted">Copy</button></div></label>
          <label className="block"><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Verify token</span>
            <div className="flex gap-1 mt-1"><code data-testid="wa-api-webhook-token" className="flex-1 text-xs bg-muted/60 rounded-lg px-2 py-1.5 break-all">{wh.verify_token || "— not set on server —"}</code><button type="button" onClick={() => { navigator.clipboard?.writeText(wh.verify_token || ""); toast.success("Copied"); }} className="rounded-lg border border-border bg-white px-2 text-xs hover:bg-muted">Copy</button></div></label>
        </div>
        <ol className="mt-2 list-decimal pl-4 space-y-0.5">
          <li>Open <a className="underline" href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer">developers.facebook.com/apps</a> → your app → <strong>WhatsApp → Configuration</strong>.</li>
          <li>Under Webhook click <strong>Edit</strong>, paste the Callback URL and Verify token above, click <strong>Verify and save</strong>.</li>
          <li>Click <strong>Manage</strong> next to Webhook fields and <strong>Subscribe</strong> to <code>messages</code>.</li>
        </ol>
      </details>
    </div>
  );
};

const TemplateComposer = ({ templates, adminKey, enabled, defaultMobile }) => {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [vars, setVars] = useState([]);
  const [sending, setSending] = useState(false);
  const tpl = templates.find((t) => t.name === name);
  const count = tpl?.var_count || 0;
  const pick = (v) => { setName(v); const t = templates.find((x) => x.name === v); setVars(Array.from({ length: t?.var_count || 0 }, () => "")); };
  const preview = tpl?.body ? tpl.body.replace(/{{\s*(\d+)\s*}}/g, (_, i) => vars[Number(i) - 1] || `{{${i}}}`) : "";
  const send = async () => {
    const to = (mobile || defaultMobile || "").trim();
    if (!name) { toast.error("Choose a template"); return; }
    if (!to) { toast.error("Enter a WhatsApp number"); return; }
    if (vars.some((v) => !v.trim())) { toast.error("Fill in all variables"); return; }
    setSending(true);
    try {
      const res = await fetch(`${API}/admin/whatsapp-api/send`, { method: "POST", headers: { "Content-Type": "application/json", "X-PL-Client": "web", "X-Admin-Key": adminKey }, body: JSON.stringify({ mobile: to, template_name: name, language: tpl?.language || "en", variables: vars }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed");
      toast.success(`"${name}" sent to ${to}`);
    } catch (err) {
      toast.error(err.message || "Send failed");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mt-3 rounded-lg bg-white/70 border border-border px-3 py-3 text-sm" data-testid="wa-api-composer">
      <p className="font-medium">Send any template to a WhatsApp number</p>
      <p className="text-[11px] text-muted-foreground mt-0.5">Pick an approved template, fill its variables and send it to one number — useful for reminders, Zoom links or re-sending a welcome.</p>
      <div className="mt-2 grid lg:grid-cols-2 gap-3">
        <div className="space-y-2">
          <TemplateSelect testId="wa-composer-template" value={name} templates={templates.filter((t) => t.status === "APPROVED")} onChange={pick} />
          <input data-testid="wa-composer-mobile" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder={defaultMobile ? `WhatsApp number (default: ${defaultMobile})` : "WhatsApp number with country code, e.g. +91 98xxxxxxxx"} className={`${inputCls} w-full`} />
          {Array.from({ length: count }, (_, i) => (
            <input key={i} data-testid={`wa-composer-var-${i + 1}`} value={vars[i] || ""} onChange={(e) => setVars((v) => { const n = [...v]; n[i] = e.target.value; return n; })} placeholder={`Variable {{${i + 1}}}`} className={`${inputCls} w-full`} />
          ))}
          <button type="button" data-testid="wa-composer-send-btn" onClick={send} disabled={sending || !enabled || !name} className="inline-flex items-center gap-1.5 rounded-full bg-[#128C7E] text-white px-5 py-2 text-xs font-medium hover:bg-[#0f7568] transition-colors disabled:opacity-50">
            <Send size={12} /> {sending ? "Sending…" : "Send now"}
          </button>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Preview</p>
          <pre data-testid="wa-composer-preview" className="whitespace-pre-wrap font-sans text-xs rounded-2xl rounded-tl-sm bg-[#E7F8EE] border border-[#BFE3CF] px-3 py-2 min-h-[80px] max-h-56 overflow-auto">{preview || "Choose a template to see its message here."}</pre>
          {tpl && <p className="text-[10px] text-muted-foreground mt-1">{tpl.category} · {tpl.language} · {count} variable{count === 1 ? "" : "s"}</p>}
        </div>
      </div>
    </div>
  );
};

const VariablesPanel = ({ content, setC, adminKey, onContentSaved }) => {
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/content`, { method: "PUT", headers: { "Content-Type": "application/json", "X-PL-Client": "web", "X-Admin-Key": adminKey },
        body: JSON.stringify({ batch_no: content.batch_no || "", wa_var_program: content.wa_var_program || "", wa_var_url: content.wa_var_url || "", wa_var_testurl: content.wa_var_testurl || "", wa_var_website: content.wa_var_website || "", wa_var_date: content.wa_var_date || "", wa_var_constant1: content.wa_var_constant1 || "", wa_var_constant2: content.wa_var_constant2 || "", wa_var_constant3: content.wa_var_constant3 || "" }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || "Failed");
      onContentSaved?.(data);
      toast.success("Template variables saved");
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };
  const Row = ({ k, desc, children }) => (
    <div className="grid grid-cols-[110px_1fr] sm:grid-cols-[130px_1fr_1.2fr] items-center gap-x-3 gap-y-1 py-2 border-t border-border/60 first:border-t-0" data-testid={`wa-var-row-${k}`}>
      <code className="text-xs font-semibold text-[#1A2E26] bg-muted/60 rounded-md px-2 py-1 w-fit">{k}</code>
      <span className="text-[11px] text-muted-foreground">{desc}</span>
      <div className="col-span-2 sm:col-span-1">{children}</div>
    </div>
  );
  return (
    <div className="rounded-lg bg-white/70 border border-border px-3 py-3 text-sm mb-3" data-testid="wa-api-variables">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-medium">Template variables</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">Use these names in the "Body variables" box below, in the order of {"{{1}}, {{2}}…"} in your template. Values are filled automatically for every message.</p>
        </div>
        <button type="button" data-testid="wa-var-save-btn" onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 rounded-full bg-[#1A2E26] text-white px-4 py-1.5 text-xs font-medium hover:opacity-90 disabled:opacity-50">{saving ? "Saving…" : "Save variables"}</button>
      </div>
      <div className="mt-2">
        <Row k="name" desc="Name of the participant who registers"><span className="text-xs text-emerald-700" data-testid="wa-var-name">Filled automatically from the registration</span></Row>
        <Row k="batch" desc="Batch # (same as Section 1 — Master Control, Future Batch#)"><input data-testid="wa-var-batch" value={content.batch_no || ""} onChange={(e) => setC("batch_no", e.target.value)} placeholder="e.g. 170" className={`${inputCls} w-full`} /></Row>
        <Row k="program" desc="Program name (leave blank to use the program chosen at registration)"><input data-testid="wa-var-program" value={content.wa_var_program || ""} onChange={(e) => setC("wa_var_program", e.target.value)} placeholder="e.g. Positive Life 6 Days Training" className={`${inputCls} w-full`} /></Row>
        <Row k="url" desc="Link to include, e.g. WhatsApp group invite (only for templates whose body accepts a link variable)"><input data-testid="wa-var-url" value={content.wa_var_url || ""} onChange={(e) => setC("wa_var_url", e.target.value)} placeholder="https://chat.whatsapp.com/…" className={`${inputCls} w-full`} /></Row>
        <Row k="testurl" desc="Test link — use it in place of url while trying out a new invite link (e.g. map a template to name,program,testurl)"><input data-testid="wa-var-testurl" value={content.wa_var_testurl || ""} onChange={(e) => setC("wa_var_testurl", e.target.value)} placeholder="https://chat.whatsapp.com/… (test group)" className={`${inputCls} w-full`} /></Row>
        <Row k="website" desc="Website link to include in messages"><input data-testid="wa-var-website" value={content.wa_var_website ?? ""} onChange={(e) => setC("wa_var_website", e.target.value)} placeholder="https://thepositivelife.org/" className={`${inputCls} w-full`} /></Row>
        <Row k="date" desc="Date text for messages, e.g. batch start date (leave blank to use the registration date)"><input data-testid="wa-var-date" value={content.wa_var_date || ""} onChange={(e) => setC("wa_var_date", e.target.value)} placeholder="e.g. 15 July 2026" className={`${inputCls} w-full`} /></Row>
        {[1, 2, 3].map((i) => (
          <Row key={i} k={`constant${i}`} desc={`Fixed text ${i} — any value you want to drop into a template (e.g. a venue, a time, a phone number)`}><input data-testid={`wa-var-constant${i}`} value={content[`wa_var_constant${i}`] || ""} onChange={(e) => setC(`wa_var_constant${i}`, e.target.value)} placeholder={`Value for constant${i}`} className={`${inputCls} w-full`} /></Row>
        ))}
        <Row k="others" desc="Also available"><span className="text-[11px] text-muted-foreground">mobile · email · place · zoom_link · zoom_meeting_id · zoom_passcode · batch_dates · batch_time — or type any fixed text</span></Row>
      </div>
    </div>
  );
};

export const WhatsAppApiPanel = ({ content, setC, adminKey, onContentSaved }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testMobile, setTestMobile] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/whatsapp-api/status`, { headers: { "X-PL-Client": "web", "X-Admin-Key": adminKey } });
      setStatus(res.ok ? await res.json() : { configured: false, error: `HTTP ${res.status}` });
    } catch (err) {
      setStatus({ configured: false, error: err.message });
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  const templates = status?.templates || [];
  const enabled = content.wa_api_enabled !== false;
  const shared = { content, setC, templates, testMobile, adminKey, enabled, onContentSaved };

  return (
    <div className="rounded-xl border border-[#128C7E]/30 bg-[#F0FAF6] p-4 mb-5" data-testid="whatsapp-api-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-medium">{status?.provider || "Meta WhatsApp Business Cloud API"}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Automatic welcome on registration, plus one-click Zoom details to every participant — all using Meta-approved templates.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Enabled</span>
          <Switch data-testid="content-wa_api_enabled" aria-label="Toggle automatic WhatsApp welcome" checked={enabled} onCheckedChange={(v) => setC("wa_api_enabled", v)} />
          <button type="button" data-testid="wa-api-refresh" onClick={load} disabled={loading} className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1 text-xs hover:bg-muted transition-colors disabled:opacity-50">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {status && !status.configured && (
        <p className="mt-3 text-sm text-red-700" data-testid="wa-api-not-configured">WhatsApp Business API is not configured on the server (META_WHATSAPP_TOKEN / META_WHATSAPP_PHONE_ID missing).</p>
      )}
      {status?.error && <p className="mt-3 text-sm text-red-700" data-testid="wa-api-error">{status.error}</p>}

      {status?.phone && (
        <div className="mt-3 rounded-lg bg-white/70 border border-border px-3 py-2 text-sm inline-flex flex-wrap gap-x-4 gap-y-1" data-testid="wa-api-phone">
          <span><span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Number</span><strong>{status.phone.display_phone_number}</strong> · {status.phone.verified_name}</span>
          <span><span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Quality</span>{status.phone.quality_rating}</span>
          {status.phone.messaging_limit_tier && <span><span className="text-[10px] uppercase tracking-widest text-muted-foreground mr-1">Limit</span>{status.phone.messaging_limit_tier.replace("TIER_", "")}/day</span>}
        </div>
      )}

      {status?.configured && <div className="mt-3"><VariablesPanel content={content} setC={setC} adminKey={adminKey} onContentSaved={onContentSaved} /></div>}
      {status?.configured && <TemplateComposer templates={templates} adminKey={adminKey} enabled={enabled} defaultMobile={testMobile.trim()} />}

      {status?.webhook && <WebhookCard wh={status.webhook} adminKey={adminKey} onChanged={load} />}

      {status?.configured && (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <span className="text-xs text-muted-foreground">Test number for "Send test" buttons:</span>
            <input data-testid="wa-api-test-mobile" value={testMobile} onChange={(e) => setTestMobile(e.target.value)} placeholder="+91 98xxxxxxxx" className={`${inputCls} w-52`} />
            <span className="text-[11px] text-muted-foreground">Save first if you changed template settings.</span>
          </div>
          <div className="mt-3 grid lg:grid-cols-2 gap-3 text-sm">
            <div className="space-y-2">
              <TemplateBlock kind="welcome" title="Welcome template" heading="I-Welcome template" hint={`Sent automatically on registration. Variables: ${VAR_HINT}.`} {...shared} />
              <label className="block rounded-lg bg-white/70 border border-border px-3 py-2" data-testid="wa-api-fallback-block">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Fallback welcome template (used while the one above is pending / unavailable)</span>
                <TemplateSelect testId="wa-api-fallback-select" value={content.wa_welcome_fallback_template || ""} templates={templates} onChange={(v) => setC("wa_welcome_fallback_template", v)} />
                <span className="mt-1.5 block text-[10px] uppercase tracking-widest text-muted-foreground">Fallback variables (comma-separated, in template order)</span>
                <input data-testid="wa-api-fallback-vars" value={content.wa_welcome_fallback_vars ?? "name,program,batch"} onChange={(e) => setC("wa_welcome_fallback_vars", e.target.value)} placeholder="name,program,batch" className="mt-1 w-full rounded-md border border-border bg-white px-2 py-1 text-xs" />
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
